import {
  calculateMonthlyPrice,
  classifyPurchaseError,
  failureOf,
  createSubscribeFlow,
  removeTrailingZeros,
  RestoreNoTransactionError,
  withFailure,
  readOwnerUid,
} from '@forest/paywall-core'
import { TrialTimelineView, useTrialTimelineDialog } from '@forest/shared/react'
import { useMutation } from '@tanstack/react-query'
import { compare } from 'compare-versions'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import { useEffect, useMemo, useState, useTransition } from 'react'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { initialRevealingRef } from '~/lib/loading'
import { posthog } from '~/lib/posthog'
import { m } from '~/paraglide/messages'
import { appVersion, platform } from '~/params'
import { api } from '~/services/api'
import { native } from '@forest/native-services'
import type { Products } from '@forest/native-services'
import { type ChinaAndroidTrialEligibility } from '~/store/china-android-trial'
import { $debugAfterPaymentError } from '~/store/debug'
import { $receiptSystemAccountId } from '~/store/receipt-system-account-id'
import { dialog } from '~/store/dialog'
import type { ProductMetadata } from '~/types/iap'
import { InterpolatedMessage } from './intepolated-message'
import { LoadingOverlay } from './loading-overlay'
import { SpotlightGraphics } from './spotlight-graphics'
import { SubscribedView } from './subscribed-view'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'

const isChinaAndroid = platform === 'china_android'

const flow = createSubscribeFlow<ProductMetadata>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) =>
      posthog?.captureException(error, context),
  },
  getUserId: async () => {
    const userInfo = await getAuthenticatedUserInfo()
    return userInfo.user_id
  },
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
  getDebugSimulate: () => $debugAfterPaymentError.get(),
})

export function FixedBottomCTA({
  products,
  chinaAndroidTrialEligibility,
}: {
  products: Products
  chinaAndroidTrialEligibility: Exclude<ChinaAndroidTrialEligibility, 'pending'>
}) {
  const [isSubscribedViewOpen, setIsSubscribedViewOpen] = useState(false)
  const [isTransitionPending, startTransition] = useTransition()
  const timeline = useTrialTimelineDialog()

  const isChinaAndroidTrialEligible =
    isChinaAndroid && chinaAndroidTrialEligibility === 'available'

  const trialDays = isChinaAndroidTrialEligible
    ? 7
    : import.meta.env.DEV
      ? 7
      : products.annualEarlyBird.eligible_trial_days

  const isTrialEligible = trialDays > 0
  const isTimelineSupported = compare(appVersion, '5.2.0', '>=')
  const shouldShowTimeline =
    !isChinaAndroid && isTrialEligible && isTimelineSupported

  const { mutateAsync, isPending: isMutationPending } = useMutation({
    mutationFn: async () => {
      if (import.meta.env.DEV) {
        return {
          skuId: 'dev-sku-id',
          serviceLevelStatus: {
            service_level: 'subscription',
            is_premium: false,
            subscription: {
              expires_at: new Date(
                Date.now() + 1000 * 60 * 60 * 24 * 30,
              ).toISOString(),
            },
          },
        }
      }

      const product = products.annualEarlyBird

      const serviceLevelStatus = await flow.run(product)
      return {
        skuId: product.sku,
        serviceLevelStatus,
      }
    },
    onSuccess: async ({ skuId, serviceLevelStatus }) => {
      native.general.logEvent({
        event_name: 'product_sku_purchased_success',
        event_params: {
          sku_id: skuId,
        },
      })
      const successEventName =
        !isChinaAndroid && isTrialEligible
          ? 'plus_trial_success'
          : 'plus_subscribe_success'
      native.general.logEvent({
        event_name: successEventName,
        event_params: {
          sku_id: skuId,
        },
      })

      let task = Promise.resolve()
      if (
        shouldShowTimeline &&
        serviceLevelStatus?.service_level === 'subscription'
      ) {
        task = (async function showGrantPermissionDialogIfNeeded() {
          if (import.meta.env.DEV) {
            await timeline.openPermissionDialog(
              serviceLevelStatus.subscription.expires_at,
            )
            return
          }
          let result
          try {
            result = await native.permission.getHasNotificationPermission()
          } catch {
            return
          }
          if (result.success && !result.data) {
            await timeline.openPermissionDialog(
              serviceLevelStatus.subscription.expires_at,
            )
          }
        })()
      }
      task.then(() => {
        setIsSubscribedViewOpen(true)
        native.user.refreshServiceLevel()
      })
    },
    onError: (error) => {
      if (error instanceof AbortError) return

      const intent = classifyPurchaseError(error)
      switch (intent.kind) {
        case 'payment_timeout':
          dialog.open({
            title: m.dialog_payment_timeout_title(),
            description: withFailure(
              m.dialog_payment_timeout_content({ error: intent.detail }),
              intent.failure,
            ),
            button: m.OK(),
          })
          return
        case 'illegal_transaction_restore':
          dialog.open({
            title: m.dialog_illegal_transaction_title(),
            description: withFailure(
              platform === 'china_android'
                ? m.dialog_illegal_transaction_content_cna()
                : m.dialog_illegal_transaction_content(),
              intent.failure,
            ),
            button: m.restore_purchase(),
            closeButton: true,
            asyncAction: async () => {
              dialog.setLoading(true)
              try {
                await flow.restore()
                dialog.close()
                dialog.open({
                  title: m.dialog_restore_success_title(),
                  description: m.dialog_restore_success_content(),
                  button: m.awesome(),
                  action: () => {
                    native.user.refreshServiceLevel()
                    native.general.close()
                  },
                })
              } catch (restoreError) {
                dialog.close()
                await showRestoreErrorDialog(restoreError)
              }
            },
          })
          return
        case 'no_internet':
          dialog.open({
            title: m.dialog_no_internet_title(),
            description: withFailure(
              m.dialog_no_internet_content(),
              intent.failure,
            ),
            button: m.OK(),
          })
          return
        case 'http_error':
          dialog.open({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            button: m.contact_cs(),
            action: () => {
              openIntercom({
                node: 'subscribe_create_purchase_unexpected_http_error',
                orderId: intent.failure.purchaseId?.toString(),
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        case 'subscription_conflict':
          void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
          return
        case 'unknown':
          dialog.open({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            button: m.contact_cs(),
            action: () => {
              openIntercom({
                node: 'subscribe_create_purchase_unexpected_error',
                orderId: intent.failure.purchaseId?.toString(),
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        default:
          intent satisfies never
      }
    },
  })

  const {
    mutateAsync: startChinaAndroidTrial,
    isPending: isChinaAndroidTrialPending,
  } = useChinaAndroidTrialMutation({
    onSuccess: async () => {
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'success',
          component_name: 'relax_paywall_trial',
        },
      })
      native.general.logEvent({
        event_name: 'plus_trial_success',
        event_params: {
          sku_id: 'cna_plus_trial_7',
        },
      })
      setIsSubscribedViewOpen(true)
    },
  })

  const buttonComponentName = useMemo(() => {
    if (isChinaAndroidTrialEligible || trialDays > 0) {
      return 'relax_paywall_trial'
    } else {
      return 'relax_paywall_subscribe'
    }
  }, [isChinaAndroidTrialEligible, trialDays])

  useEffect(() => {
    let abort = false
    initialRevealingRef.current.promise.then(() => {
      if (abort) return
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'show',
          component_name: buttonComponentName,
        },
      })
    })
    return () => {
      abort = true
    }
  }, [buttonComponentName])

  const isActionPending =
    isMutationPending || isChinaAndroidTrialPending || isTransitionPending

  const startTrial = async () => {
    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        component_name: 'relax_paywall_trial_timeline',
        app_action: 'click',
        type: 'btn',
      },
    })
    if (isMutationPending) return
    await mutateAsync()
  }

  const getTrialTimelinePrice = () => {
    return removeTrailingZeros(products.annualEarlyBird.price_localized)
  }

  const handleButtonClick = () => {
    if (shouldShowTimeline) {
      timeline.openTimeline()
      native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name: 'relax_paywall_trial_timeline',
        },
      })
      return
    }

    startTransition(async () => {
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'click',
          component_name: buttonComponentName,
        },
      })
      if (!products) return
      if (isChinaAndroidTrialEligible) {
        await startChinaAndroidTrial().catch(() => {})
      } else {
        await mutateAsync().catch(() => {})
      }
    })
  }

  const getButtonContent = () => {
    if (platform === 'china_android') {
      if (chinaAndroidTrialEligibility === 'available') {
        return m.onboarding_paywall_start_triasl_btn_cna({
          trial_day_number: 7,
        })
      } else {
        return (
          <InterpolatedMessage
            message={m.relax_paywall_purchase_btn_cna}
            inputs={{
              original_yearly_price: (
                <span className="line-through">
                  {removeTrailingZeros(products.annualOriginal.price_localized)}
                </span>
              ),
              discount_yearly_price: removeTrailingZeros(
                products.annualEarlyBird.price_localized,
              ),
            }}
          />
        )
      }
    }

    if (trialDays > 0) {
      return m.daily_paywall_tree_cta_btn()
    } else {
      return (
        <InterpolatedMessage
          message={m.yearly_subscription_btn}
          inputs={{
            original_yearly_price: (
              <span className="line-through">
                {removeTrailingZeros(products.annualOriginal.price_localized)}
              </span>
            ),
            discount_yearly_price: removeTrailingZeros(
              products.annualEarlyBird.price_localized,
            ),
          }}
        />
      )
    }
  }

  const getFooterContent = () => {
    if (platform === 'china_android') {
      if (chinaAndroidTrialEligibility === 'available') {
        return m.onboarding_paywall_footer_trial_cna()
      } else {
        return m.relax_paywall_purchase_footer_cna({
          view_all_plans: '',
          yearly_price_month: calculateMonthlyPrice(products.annualEarlyBird),
        })
      }
    }
    if (trialDays > 0) {
      return m.free_trial_cta_footer({
        view_all_plans: '',
        trial_day: trialDays,
        yearly_price: removeTrailingZeros(
          products.annualEarlyBird.price_localized,
        ),
      })
    } else {
      return m.yearly_subscription_footer({
        view_all_plans: '',
        yearly_price_month: calculateMonthlyPrice(products.annualEarlyBird),
      })
    }
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-20 h-[200px]"
        style={{
          backgroundImage: 'linear-gradient(to bottom, #0000, #000F)',
        }}
      />
      <div className="fixed bottom-0 left-0 right-0 z-20 h-[200px]">
        <ProgressiveBlur />
      </div>

      <div
        id="fixed-bottom-cta"
        className="fixed bottom-0 left-0 right-0 z-20 flex h-[200px] translate-y-full flex-col items-center justify-end gap-3 px-6 pb-12"
      >
        <button
          className="group h-[52px] w-full max-w-[640px] rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
          onClick={handleButtonClick}
          disabled={isActionPending}
        >
          <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
            {getButtonContent()}
          </span>
        </button>

        <p className="w-full text-center text-sm text-white">
          {getFooterContent()}
        </p>
      </div>

      <TrialTimelineView
        {...timeline.timelineProps}
        m={m}
        backgroundLayer={
          <div
            className="relative size-full"
            style={{
              backgroundImage: 'linear-gradient(172deg, #13193A 0%, #000 100%)',
            }}
          >
            <SpotlightGraphics />
          </div>
        }
        eligibleTrialDays={trialDays}
        planPrice={getTrialTimelinePrice()}
        startTrial={startTrial}
        requestNotificationPermission={async () => {
          try {
            await native.permission.requestNotificationPermission()
          } catch {
            // ignore
          }
        }}
      />

      <LoadingOverlay isOpen={isActionPending} />

      <SubscribedView
        isOpen={isSubscribedViewOpen}
        isTrial={isChinaAndroidTrialEligible}
      />
    </>
  )
}

async function showRestoreErrorDialog(error: unknown) {
  const failure = failureOf(error, 'restore')
  if (error instanceof RestoreNoTransactionError) {
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_restore_no_transaction_content({
          app_store_account:
            platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
        }),
        failure,
      ),
      button: m.OK(),
    })
    return
  }

  if (isHTTPError(error)) {
    const status = error.response.status
    if ((status === 409 || status === 412) && platform !== 'china_android') {
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return
    }
    if (status === 403 && platform !== 'china_android') {
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account:
              platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
          }),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    if (
      status === 410 ||
      ((status === 403 || status === 409) && platform === 'china_android')
    ) {
      dialog.open({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_unknown_error_content({ error_code: failure.code }),
        failure,
        { codeInDescription: true },
      ),
      button: m.contact_cs(),
      action: () => {
        openIntercom({
          node: 'restore_purchase_unexpected_http_error',
          orderId: failure.purchaseId?.toString(),
          additional: { error_code: failure.code },
        })
      },
    })
    return
  }

  if (isNetworkError(error) || isTimeoutError(error)) {
    dialog.open({
      title: m.dialog_no_internet_title(),
      description: withFailure(m.dialog_no_internet_content(), failure),
      button: m.OK(),
    })
    return
  }

  dialog.open({
    title: m.dialog_restore_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    button: m.contact_cs(),
    action: () => {
      openIntercom({
        node: 'restore_purchase_unexpected_error',
        orderId: failure.purchaseId?.toString(),
        additional: { error_code: failure.code },
      })
    },
  })
}

function ProgressiveBlur() {
  const maskGradients = [
    [
      'rgba(0, 0, 0, 0) 0%',
      'rgba(0, 0, 0, 1) 12.5%',
      'rgba(0, 0, 0, 1) 25%',
      'rgba(0, 0, 0, 0) 37.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 12.5%',
      'rgba(0, 0, 0, 1) 25%',
      'rgba(0, 0, 0, 1) 37.5%',
      'rgba(0, 0, 0, 0) 50%',
    ],
    [
      'rgba(0, 0, 0, 0) 25%',
      'rgba(0, 0, 0, 1) 37.5%',
      'rgba(0, 0, 0, 1) 50%',
      'rgba(0, 0, 0, 0) 62.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 37.5%',
      'rgba(0, 0, 0, 1) 50%',
      'rgba(0, 0, 0, 1) 62.5%',
      'rgba(0, 0, 0, 0) 75%',
    ],
    [
      'rgba(0, 0, 0, 0) 50%',
      'rgba(0, 0, 0, 1) 62.5%',
      'rgba(0, 0, 0, 1) 75%',
      'rgba(0, 0, 0, 0) 87.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 62.5%',
      'rgba(0, 0, 0, 1) 75%',
      'rgba(0, 0, 0, 1) 87.5%',
      'rgba(0, 0, 0, 0) 100%',
    ],
    ['rgba(0, 0, 0, 0) 75%', 'rgba(0, 0, 0, 1) 87.5%', 'rgba(0, 0, 0, 1) 100%'],
    ['rgba(0, 0, 0, 0) 87.5%', 'rgba(0, 0, 0, 1) 100%'],
  ]
  const maxBlur = 18
  const blurStep = maxBlur / (maskGradients.length - 1)

  return maskGradients.map((mask, index) => {
    const blur = Math.round(blurStep * index * 1000) / 1000
    const maskImage = `linear-gradient(to bottom, ${mask.join(', ')})`
    return (
      <div
        key={`${mask.join('-')}-${blur}`}
        className="absolute inset-0"
        style={{
          zIndex: index + 1,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          maskImage,
          WebkitMaskImage: maskImage,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskSize: '100% 100%',
          WebkitMaskSize: '100% 100%',
          willChange: 'backdrop-filter',
        }}
      />
    )
  })
}
